/**
 * Pure interleaving logic for the "Conversation reset" marker in the chat thread.
 *
 * Kept out of MessageList.tsx on purpose (mirrors the split PR #296 made between
 * src/lib/run-observability/ai-auto-status.ts and AIAutoModeStatus.tsx) so the placement logic is
 * testable without rendering React.
 *
 * The marker's source data is <schema>.conversation_events (event_type = 'conversation_reset'),
 * never <schema>.messages — see supabase/migrations/20260814140000_add_conversation_reset_markers_rpc.sql
 * for why that separation is what makes it structurally impossible for a reset marker to reach
 * WhatsApp: nothing that forwards messages to WhatsApp reads conversation_events, so a value that
 * never becomes a `messages` row can never enter that path. This module only decides WHERE in the
 * on-screen stream a marker belongs relative to the (separately fetched) messages — it never
 * writes anything back.
 */

export interface ConversationResetMarker {
  id: string;
  /** ISO 8601 timestamp — matches conversation_events.created_at for the underlying reset. */
  resetAt: string;
}

export interface ResetMarkerStreamItem {
  type: 'reset';
  id: string;
  resetAt: string;
}

/**
 * Interleaves reset markers into an already-time-ordered list of items, placing each marker
 * immediately before the first item that happened after it. A marker with nothing after it (the
 * conversation was reset and nothing has happened since — the exact case that prompted this
 * feature: the operator resets, then sees no trace of it) is appended at the end of the stream.
 *
 * `items` MUST already be sorted ascending by the timestamp `getTimestamp` reads. `markers` does
 * not need to be pre-sorted — this sorts its own copy and never mutates its inputs.
 */
export function interleaveResetMarkers<T>(
  items: readonly T[],
  markers: readonly ConversationResetMarker[],
  getTimestamp: (item: T) => string,
): Array<T | ResetMarkerStreamItem> {
  const sortedMarkers = [...markers].sort(
    (a, b) => new Date(a.resetAt).getTime() - new Date(b.resetAt).getTime(),
  );

  const result: Array<T | ResetMarkerStreamItem> = [];
  let markerIndex = 0;

  const flushMarkersUpTo = (cutoffMs: number | null) => {
    while (
      markerIndex < sortedMarkers.length &&
      (cutoffMs === null || new Date(sortedMarkers[markerIndex].resetAt).getTime() <= cutoffMs)
    ) {
      const marker = sortedMarkers[markerIndex];
      result.push({ type: 'reset', id: marker.id, resetAt: marker.resetAt });
      markerIndex++;
    }
  };

  for (const item of items) {
    flushMarkersUpTo(new Date(getTimestamp(item)).getTime());
    result.push(item);
  }
  // Anything left over happened after the last item in the list (including the case where there
  // were no items at all) — still needs to show, at the end of the stream.
  flushMarkersUpTo(null);

  return result;
}

export function isResetMarkerStreamItem(item: unknown): item is ResetMarkerStreamItem {
  return (
    typeof item === 'object' &&
    item !== null &&
    (item as { type?: unknown }).type === 'reset'
  );
}
